import { useCallback, useEffect, useState, type FC } from "react"
import { createPortal } from "react-dom"
import { useMap } from "react-leaflet"
import { DomEvent, type LatLngBounds } from "leaflet"
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong"
import { Box, IconButton, Tooltip } from "@mui/material"

interface RecenterControlProps {
  // Null while the geometry is still loading.
  bounds: LatLngBounds | null
  title: string
}

const FIT_PADDING: [number, number] = [16, 16]

/**
 * "Show me the whole line again", rendered into leaflet's own top-right control
 * corner instead of positioned over the map. Leaflet stacks that corner, so the
 * button sits under the layers toggle and stays there as the layers list grows.
 */
const RecenterControl: FC<RecenterControlProps> = ({ bounds, title }) => {
  const map = useMap()
  const [corner, setCorner] = useState<HTMLElement | null>(null)

  useEffect(() => {
    setCorner(map.getContainer().querySelector<HTMLElement>(".leaflet-top.leaflet-right"))
  }, [map])

  const handleClick = useCallback(() => {
    if (!bounds) return
    // A popup left open would cover the view we just framed, still pointing at
    // a span you can no longer pick out.
    map.closePopup()
    map.fitBounds(bounds, { padding: FIT_PADDING })
  }, [map, bounds])

  if (!corner || !bounds) return null

  return createPortal(
    <Box
      className="leaflet-control leaflet-bar"
      // Otherwise the click reaches the map too, which zooms on a double tap.
      ref={(node: HTMLElement | null) => {
        if (node) DomEvent.disableClickPropagation(node)
      }}
      // Leaflet's controls are white; map.css themes them dark. Matching that
      // here keeps the corner one cluster instead of two mismatched buttons.
      sx={{
        backgroundColor: "grey.800",
        border: "1px solid",
        borderColor: "grey.700",
        borderRadius: "4px",
        overflow: "hidden",
      }}
    >
      <Tooltip title={title} placement="left">
        <IconButton
          aria-label={title}
          onClick={handleClick}
          sx={{
            width: 36,
            height: 36,
            borderRadius: 0,
            color: "text.primary",
            "&:hover": { backgroundColor: "grey.700" },
          }}
        >
          <CenterFocusStrongIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>,
    corner,
  )
}

export default RecenterControl
